/*
 * Copyright (c) 2022 CalAmp Corp. All Rights Reserved
 */
import React, {useCallback} from 'react';
import {makeStyles} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import {GroupChooser} from '@ion/dls/src/editors/GroupChooser';
import {AdminGroupChooser} from '@ion/features/src/entities/group/AdminGroupChooser';
import {GroupAssignmentAccountChooserProps} from './GroupAssignmentAccountChooser';
import {
  GroupAssignmentDetailsFields,
  GroupAssignmentFormProps
} from './GroupAssignmentDetailsForm';
import {GroupAssignmentActions} from './types';

export interface AddGroupAssignmentActionProps extends
  GroupAssignmentAccountChooserProps,
  GroupAssignmentFormProps {}

const useStyles = makeStyles(() => ({
  paddingTop : {
    paddingTop : '15px'
  }
}));

/**
 * Re-usable hook for adding group.
 */
export const AddGroupAssignmentAction = ({
  groupAction,
  account,
  handleAccountGroupChooser,
  handleGroupChooser,
  allGroups,
  fromGroup,
  toGroup,
  formState,
  path,
  isNewGroup = false,
  multiSelect = false,
  areGroupsLoading = false,
  isSelectedGridList = false
}: AddGroupAssignmentActionProps) => {
  const classes       = useStyles();
  const isGroupIdSame = fromGroup?.id === toGroup?.id;

  const getFieldLabel = useCallback(() => {
    return groupAction === GroupAssignmentActions.Replace
      ? translate(`${path}.toGroup`) : '';
  }, [groupAction]);

  return (
    <>
      <div className={classes.paddingTop}>
        <GroupChooser required
          account={account}
          includeGroupProperties={false}
          onChange={handleAccountGroupChooser}
          hasError={formState[GroupAssignmentDetailsFields.ACCOUNT]?.error}
          defaultValue={account || formState[GroupAssignmentDetailsFields.ACCOUNT]?.value ||
            formState[GroupAssignmentDetailsFields.ADD_GROUP]?.value} />
      </div>
      <div className={classes.paddingTop}>
        <AdminGroupChooser isAdd
          required
          multiSelect={multiSelect}
          includeNone={isSelectedGridList}
          noneOptionValue={isSelectedGridList ? 'newGroup' : ''}
          noneOptionLabel={isSelectedGridList ? translate(`${path}.newGroup`) : ''}
          fieldLabel={getFieldLabel()}
          groupOptions={allGroups}
          loading={areGroupsLoading}
          onChange={handleGroupChooser}
          hasError={!isNewGroup && (
            groupAction === GroupAssignmentActions.Add
              ? formState[GroupAssignmentDetailsFields.ADD_GROUP]?.error
              : (formState[GroupAssignmentDetailsFields.REPLACE_TO_GROUP]?.error ||
                (fromGroup && toGroup && isGroupIdSame))
          )}
          errorText={(fromGroup && toGroup && isGroupIdSame)
            ? translate(`${path}.groupsNotSame`)
            : translate(`${path}.error`)
          }
          defaultValue={(groupAction === GroupAssignmentActions.Add
            ? formState[GroupAssignmentDetailsFields.ADD_GROUP]?.value
            : formState[GroupAssignmentDetailsFields.REPLACE_TO_GROUP]?.value) || ''} />
      </div>
    </>
  );
};

export default AddGroupAssignmentAction;
